/**
 * Application Insights Fallback Configuration
 * 
 * This provides a minimal, stable Application Insights setup
 * that avoids common webpack and fetch monitoring conflicts
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsightsMinimal: ApplicationInsights | null = null;

export const initializeMinimalAppInsights = () => {
  // Only initialize on client side and if not already initialized
  if (typeof window === 'undefined' || appInsightsMinimal) {
    return appInsightsMinimal;
  }

  // Get connection string from environment variable
  const connectionString = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;
  
  if (!connectionString) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Application Insights connection string not found');
    }
    return null;
  }

  try {
    // Minimal configuration that avoids fetch monitoring issues
    appInsightsMinimal = new ApplicationInsights({
      config: {
        connectionString,
        // Disable all automatic instrumentation to avoid conflicts
        disableAjaxTracking: true,
        disableFetchTracking: true,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
        enableAutoRouteTracking: false,
        // Keep only essential tracking
        enableUnhandledPromiseRejectionTracking: true,
        // Minimal logging
        loggingLevelTelemetry: 0,
        loggingLevelConsole: 0,
        // Disable telemetry in development if desired
        disableTelemetry: process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DISABLE_TELEMETRY === 'true',
        // Stability settings
        samplingPercentage: 100,
        maxBatchSizeInBytes: 10000,
        maxBatchInterval: 30000,
      }
    });

    appInsightsMinimal.loadAppInsights();

    if (process.env.NODE_ENV === 'development') {
      console.log('Minimal Application Insights initialized successfully');
    }

    return appInsightsMinimal;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to initialize minimal Application Insights:', error);
    }
    appInsightsMinimal = null;
    return null;
  }
};

// Simple tracking functions without advanced features
export const trackEventMinimal = (name: string, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined' || !appInsightsMinimal) return;
  
  try {
    appInsightsMinimal.trackEvent({
      name,
      properties: {
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  } catch (error) {
    // Silent fallback
  }
};

export const trackErrorMinimal = (error: Error, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined' || !appInsightsMinimal) return;
  
  try {
    appInsightsMinimal.trackException({
      exception: error,
      properties: {
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  } catch (trackingError) {
    // Silent fallback
  }
};

export const trackPageViewMinimal = (name?: string, url?: string) => {
  if (typeof window === 'undefined' || !appInsightsMinimal) return;
  
  try {
    appInsightsMinimal.trackPageView({
      name,
      uri: url || (typeof window !== 'undefined' ? window.location.href : undefined)
    });
  } catch (error) {
    // Silent fallback
  }
};

export { appInsightsMinimal };